<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <h2>{{ t('workshopAppointments') }}</h2>
      <div class="flex items-center gap-4">
        <Checkbox v-model="showPastWorkshopAppointments" :label="t('showPastAppointments')" />
        <CVButton v-if="canCreate" @click.prevent="openCreateWorkshopAppointment">
          {{ t('createWorkshopAppointment') }}
        </CVButton>
      </div>
    </div>
    <WorkshopAppointmentsSummaryList
      :car-id="car.id"
      :show-past-workshop-appointments="showPastWorkshopAppointments"
      :clickable="clickable"
    />
    <WorkshopDueTasksOverview
      class="mt-3 rounded-md bg-white p-3"
      :car="car"
      @open-workshop-appointment="openEditWorkshopAppointment"
    />
  </div>
  <Modal
    :model-value="showCreateWorkshopAppointmentModal"
    :show-close="true"
    :close-on-click-outside="false"
    @update:model-value="showCreateWorkshopAppointmentModal = $event"
  >
    <div
      v-if="showCreateWorkshopAppointmentModal"
      class="max-h-[min(70vh,calc(100vh-8rem))] overflow-y-auto overscroll-contain"
    >
      <WorkshopAppointmentForm
        :car="car"
        @open-workshop-appointment="openEditWorkshopAppointment"
        @complete="showCreateWorkshopAppointmentModal = false"
      />
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import type { Car } from '@/entities/cars/car.entity';
import type { WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import WorkshopAppointmentsSummaryList from './WorkshopAppointmentsSummaryList.vue';
import WorkshopDueTasksOverview from '../vehicle-pool-list/components/WorkshopDueTasksOverview.vue';
import WorkshopAppointmentForm from '../vehicle-pool-list/components/WorkshopAppointmentForm.vue';
import Checkbox from '@/components/Checkbox.vue';
import Modal from '@/components/Modal.vue';

const props = withDefaults(
  defineProps<{
    car: Car;
    canCreate?: boolean;
    clickable?: boolean;
  }>(),
  {
    canCreate: true,
    clickable: true,
  },
);

const { t } = useI18n();
const router = useRouter();
const showPastWorkshopAppointments = ref(false);
const showCreateWorkshopAppointmentModal = ref(false);

const openCreateWorkshopAppointment = () => {
  showCreateWorkshopAppointmentModal.value = true;
};

const openEditWorkshopAppointment = async (appointment: WorkshopAppointment) => {
  if (!props.clickable) return;
  showCreateWorkshopAppointmentModal.value = false;
  await router.push({ name: 'editWorkshopAppointment', params: { id: appointment.id } });
};
</script>

<i18n lang="json">
{
  "en": {
    "workshopAppointments": "Workshop appointments",
    "createWorkshopAppointment": "Create workshop appointment",
    "showPastAppointments": "Show past appointments"
  },
  "de": {
    "workshopAppointments": "Werkstatttermine",
    "createWorkshopAppointment": "Werkstatttermin erstellen",
    "showPastAppointments": "Vergangene Termine anzeigen"
  }
}
</i18n>
